import { apiClient } from './apiClient';

export interface WorkflowRun {
  id: string;
  runId: string;
  tenantId: string;
  workflowName: string;
  scheduledFor: string;
  startedAt: string;
  completedAt?: string;
  status: 'RUNNING' | 'COMPLETED' | 'FAILED' | 'PARTIAL';
  summary?: {
    totalTasks?: number;
    created?: number;
    updated?: number;
    failed?: number;
  };
  error?: {
    message: string;
    code?: string;
  };
  createdAt: string;
  updatedAt: string;
  _count?: {
    steps: number;
  };
}

export interface WorkflowRunStep {
  id: string;
  stepId: string;
  runId: string;
  sequenceNo: number;
  stepType: 'EXTRACTION_START' | 'EXTRACTION_DONE' | 'AUDIT_ITEM' | 'UPSERT_TASK' | 'SEND_EMAIL' | 'EMAIL_SKIPPED' | 'ERROR';
  status: 'STARTED' | 'SUCCESS' | 'FAILED' | 'SKIPPED';
  timestamp: string;
  details?: Record<string, any>;
  createdAt: string;
}

export interface WorkflowRunsResponse {
  success: boolean;
  data: {
    workflowRuns: WorkflowRun[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalCount: number;
      hasNext: boolean;
      hasPrev: boolean;
      limit: number;
    };
  };
}

export interface WorkflowRunResponse {
  success: boolean;
  data: {
    workflowRun: WorkflowRun;
  };
}

export interface WorkflowStepsResponse {
  success: boolean;
  data: {
    steps: WorkflowRunStep[];
  };
}

export interface WorkflowRunFilters {
  from?: string;
  to?: string;
  status?: 'RUNNING' | 'COMPLETED' | 'FAILED' | 'PARTIAL';
  page?: number;
  limit?: number;
}

/**
 * Get workflow runs with filters
 */
export const getWorkflowRuns = async (
  filters: WorkflowRunFilters,
  token: string
): Promise<WorkflowRunsResponse> => {
  const queryParams: Record<string, string> = {};

  if (filters.from) queryParams.from = filters.from;
  if (filters.to) queryParams.to = filters.to;
  if (filters.status) queryParams.status = filters.status;
  if (filters.page) queryParams.page = String(filters.page);
  if (filters.limit) queryParams.limit = String(filters.limit);

  const response = await apiClient('workflow-runs', {
    method: 'GET',
    token,
    queryParams,
  });

  return response.json();
};

/**
 * Get workflow run by runId
 */
export const getWorkflowRun = async (
  runId: string,
  token: string
): Promise<WorkflowRunResponse> => {
  const response = await apiClient(`workflow-runs/${runId}`, {
    method: 'GET',
    token,
  });

  return response.json();
};

/**
 * Get all steps for a workflow run
 */
export const getWorkflowSteps = async (
  runId: string,
  token: string
): Promise<WorkflowStepsResponse> => {
  const response = await apiClient(`workflow-runs/${runId}/steps`, {
    method: 'GET',
    token,
  });
  return response.json();
};

/**
 * Trigger excel extraction workflow
 */
export const triggerExcelExtraction = async (
  token: string
): Promise<{ success: boolean; message?: string }> => {
  const response = await apiClient('excel-extraction/extract', {
    method: 'POST',
    token,
  });

  return response.json();
};

/**
 * Upload audit files
 */
export const uploadAuditFiles = async (
  formData: FormData,
  token: string
): Promise<{ success: boolean; message?: string }> => {
  const baseUrl = import.meta.env.VITE_API_URL;
  const response = await fetch(`${baseUrl}charts/upload?uploadToAuditFiles=true`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Upload failed');
  }

  if (response.status === 201) {
    return { ...await response.json(), success: true };
  }
  return response.json();
};

